import * as React from 'react';
import { Box } from '@mui/system';
import { Tab, Tabs, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/sharp-solid-svg-icons';
import { MultiStepContextProps } from './types';
import type { MultiStepExtraTab } from './MultiStepForm';

type MultiStepFormHeaderProps = MultiStepContextProps & {
    /** Message d'erreur transverse par étape (même ordre que `steps`) ; `undefined` si valide. */
    stepErrors?: (string | undefined)[];
    /** Onglets additionnels (lecture seule) poussés à droite, après les étapes. */
    extraTabs?: MultiStepExtraTab[];
    /** Index de l'onglet actif (étapes puis onglets additionnels). */
    activeValue: number;
    /** Sélection d'un onglet (étape ou onglet additionnel). */
    onSelectTab: (value: number) => void;
};

/**
 * En-tête du formulaire : onglets de navigation (clic libre entre les sections).
 * Remplace l'ancien stepper de wizard — chaque onglet persiste les valeurs courantes
 * avant de basculer (via `goToStep`).
 *
 * Un onglet dont la validation transverse échoue affiche une icône d'alerte
 * (couleur d'avertissement + tooltip décrivant le problème).
 */
const MultiStepFormHeader: React.FunctionComponent<MultiStepFormHeaderProps> = function (props) {
    const { steps, stepErrors = [], extraTabs = [], activeValue, onSelectTab } = props;

    return (
        <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
                value={activeValue}
                onChange={(_, value) => onSelectTab(value as number)}
                variant="scrollable"
                scrollButtons="auto"
            >
                {steps.map(({ title }, stepIndex) => {
                    const error = stepErrors[stepIndex];
                    return (
                        <Tab
                            key={title}
                            label={title}
                            iconPosition="end"
                            icon={
                                error ? (
                                    <Tooltip title={error}>
                                        <Box
                                            component="span"
                                            sx={{ display: 'inline-flex', color: 'warning.main' }}
                                        >
                                            <FontAwesomeIcon icon={faTriangleExclamation} />
                                        </Box>
                                    </Tooltip>
                                ) : undefined
                            }
                            sx={error ? { color: 'warning.main' } : undefined}
                        />
                    );
                })}
                {extraTabs.map((tab, extraIndex) => (
                    <Tab
                        key={tab.tabName}
                        label={tab.title}
                        // Le premier onglet additionnel pousse lui-même (et les suivants) à droite.
                        sx={extraIndex === 0 ? { marginLeft: 'auto' } : undefined}
                    />
                ))}
            </Tabs>
        </Box>
    );
};

export default MultiStepFormHeader;
